import ApiInstance from './intercepter';
import API from '../config/api';
import { DEFAULT_STAFF_FORM_SCHEMA } from '../config/staffFormSchema';

// Staff form schema API (user/staffFormSchema/*). ApiInstance inherits both the accessToken
// and Authorization headers from authService.js, so the two authenticated routes need no
// extra work here.
//
// initStaffFormSchema/fetchStaffFormSchema resolve to `{ sections, currentVersion }` — the form
// is now snapshotted on every change, and currentVersion is what callers echo back on submit so
// the backend validates and stamps against the exact form the client rendered rather than
// whatever is current at submit time. The mutation helpers (add/toggle/remove/move) and
// resetStaffFormSchema still resolve to a bare `sections` array — their endpoints don't return
// currentVersion, and their only caller just replaces its local state with it. Both sections and
// currentVersion can be null — only for a camp whose form has not been created yet.

// The backend ships no built-in default template: whatever the client posts to init/reset
// becomes the camp's form. `order` is required on every question and DEFAULT_STAFF_FORM_SCHEMA
// carries none, so it is injected from array position — without it the payload comes back with
// a validation error per question and no form is ever created.
const serializeDefaultSections = () => DEFAULT_STAFF_FORM_SCHEMA.map(section => ({
    ...section,
    questions: section.questions.map((question, index) => ({ ...question, order: index })),
}));

// Errors arrive as HTTP 200 with statusCode 0, so nothing below can branch on res.ok.
// `fieldErrors[].message` names the question's label rather than its key, which makes it safe
// to show an applicant as-is.
export class StaffFormError extends Error {
    constructor(error) {
        super(error?.responseMessage || 'Something went wrong. Please try again.');
        this.name = 'StaffFormError';
        this.errorCode = error?.errorCode;
        this.fieldErrors = error?.errors || [];
    }
}

/*
 * Every endpoint in this file resolves through here.
 *
 * There used to be a second, narrower `unwrapSections` that returned only the sections array, on
 * the stated grounds that "the mutation endpoints don't return currentVersion". That was simply
 * wrong: mutateQuestion and resetForm both return `form.toObject()`, and snapshotSections sets
 * `form.currentVersion` on the in-memory copy before they do — deliberately, so the response
 * carries the version it just minted. Discarding it here is what left the builder's version
 * dropdown showing the previous number until the page was refreshed.
 *
 * A camp with no form yet is a success carrying result: null, not a failure, so every field
 * below can legitimately come back null.
 *
 * `version` is the version the response actually describes, and it is null on a plain read: that
 * returns the form document itself, which carries currentVersion but no version of its own. Only
 * a ?version=N read echoes one back, which is what lets the builder confirm it is showing the
 * version it asked for rather than assuming the request landed.
 */
const unwrapSchema = res => {
    const body = res?.data;
    if (body?.statusCode !== 1) {
        throw new StaffFormError(body?.error);
    }
    const result = body?.responseData?.result;
    return {
        sections: result?.sections ?? null,
        /*
         * The director's unpublished changes, or null when there are none.
         *
         * Only ever populated on the director-gated routes — the builder read,
         * the mutations, publish and discard. The public init/GET strip it
         * server-side, so the applicant form sees null here and renders the
         * published questions, which is the whole point of the draft.
         */
        draftSections: result?.draftSections ?? null,
        currentVersion: result?.currentVersion ?? null,
        version: result?.version ?? null,
    };
};

// Idempotent — once a form exists the posted sections are ignored and the stored form is
// returned, so this is safe to call on every page load and cannot overwrite a director's work.
export const initStaffFormSchema = campCode => ApiInstance
    .post(API.STAFF_FORM_INIT, { campCode, sections: serializeDefaultSections() })
    .then(unwrapSchema);

// Returns every question including enabled: false ones — the builder needs them so a hidden
// question can be re-enabled; the applicant form filters on `enabled` when rendering.
//
// `version` reads a historical snapshot instead of the live form. Snapshots are immutable, so what
// comes back can only ever be displayed — there is no endpoint that writes to one, which is what
// makes the builder's history browsing safe by construction rather than by the UI being careful.
export const fetchStaffFormSchema = (campCode, version) => ApiInstance
    .get(`${API.STAFF_FORM_SCHEMA}?campCode=${encodeURIComponent(campCode)}${
        version == null ? '' : `&version=${encodeURIComponent(version)}`}`)
    .then(unwrapSchema);

// Metadata only — [{ version, createdAt }], newest first. Deliberately carries no `sections`: a
// camp can accumulate dozens of versions (ItFikiE has 24), and shipping every one's questions to
// populate a dropdown would be hundreds of KB for a list of numbers and dates.
export const listStaffFormVersions = campCode => ApiInstance
    .get(`${API.STAFF_FORM_VERSIONS}?campCode=${encodeURIComponent(campCode)}`)
    .then(res => {
        const body = res?.data;
        if (body?.statusCode !== 1) {
            throw new StaffFormError(body?.error);
        }
        return body?.responseData?.result ?? [];
    });

// The builder's own read of the form, including the unpublished draft. Separate from
// fetchStaffFormSchema because that one is public and strips the draft; this one is
// director-gated. Does NOT create a missing form — the builder still calls init first for that.
export const fetchStaffFormDraft = campCode => ApiInstance
    .get(`${API.STAFF_FORM_DRAFT}?campCode=${encodeURIComponent(campCode)}`)
    .then(unwrapSchema);

// add/delete/toggle/move are one endpoint switched by `action`. Each writes to the DRAFT and
// returns the whole form with `order` renumbered 0..n-1, so callers replace their working copy
// from `draftSections`. No version is minted here and `currentVersion` does not move —
// publishStaffFormDraft is the only thing that changes either.
const mutateQuestion = payload => ApiInstance.post(API.STAFF_FORM_QUESTION, payload).then(unwrapSchema);

// No questionKey — the server generates it.
export const addStaffFormQuestion = (campCode, sectionKey, question) => mutateQuestion({
    campCode, sectionKey, action: 'add', ...question,
});

// No `enabled` — the server flips whatever it has stored.
export const toggleStaffFormQuestion = (campCode, sectionKey, questionKey) => mutateQuestion({
    campCode, sectionKey, action: 'toggle', questionId: questionKey,
});

export const removeStaffFormQuestion = (campCode, sectionKey, questionKey) => mutateQuestion({
    campCode, sectionKey, action: 'delete', questionId: questionKey,
});

// direction is -1 or 1. At the first/last position the server is a silent no-op: success, form
// unchanged.
export const moveStaffFormQuestion = (campCode, sectionKey, questionKey, direction) => mutateQuestion({
    campCode, sectionKey, action: 'move', questionId: questionKey, direction,
});

// Not a bare reset — the server has no default to fall back on, so the full template goes with it.
//
// Stages the template as the DRAFT rather than replacing the live form. It is the most destructive
// button on the page, and routing it through the draft is what makes it discardable: a director who
// hits it by accident presses Discard instead of rebuilding the camp's questions by hand.
export const resetStaffFormSchema = campCode => ApiInstance
    .post(API.STAFF_FORM_RESET, { campCode, sections: serializeDefaultSections() })
    .then(unwrapSchema);

/*
 * Publishes the draft: it becomes the live form and ONE version is snapshotted for the whole
 * editing session, however many edits went into it.
 *
 * currentVersion can go DOWN — publishing a shape the camp has held before reuses that older
 * version rather than minting a new one, which is what keeps staff records submitted against it
 * rendering correctly. Callers must not assume it only climbs.
 *
 * Idempotent: publishing with no draft open succeeds and changes nothing.
 */
export const publishStaffFormDraft = campCode => ApiInstance
    .post(API.STAFF_FORM_PUBLISH, { campCode })
    .then(unwrapSchema);

// Throws the draft away; the live form and its version history are untouched, because nothing was
// ever published from it. Idempotent for the same reason publish is.
export const discardStaffFormDraft = campCode => ApiInstance
    .post(API.STAFF_FORM_DISCARD, { campCode })
    .then(unwrapSchema);
